import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Descriptions,
  Divider,
  Drawer,
  Grid,
  Input,
  message,
  Modal,
  Space,
  Table,
  Tag,
  Timeline,
  Upload,
} from "antd";
import {
  CheckCircleFilled,
  CloseCircleOutlined,
  DownloadOutlined,
  MailOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useFirebase, useFirestore } from "react-redux-firebase";
import * as dayjs from "dayjs";
import { DATE_FORMAT_DD_MM_YYYY_HH_mm } from "../../../constants/DateConstant";
import { FIRESTORE_USERS_TABLE } from "../../../constants/FirestoreConstant";
import { formatAddress, formatPrice, getPaymentStatus, getPaymentStatusColor } from "../../../functions/formatters";
import { CURRENCY } from "../../../constants/DeliveryConstants";
import { getSalesPersonNameById } from "../../../functions/users";
import { colors } from "../../../utils/theme";
import styled from "styled-components";
import { useCrmNotes } from "../../../data/hooks/useCrmNotes";
import CrmContactMomentModal from "./CrmContactMomentModal";
import CrmTaskModal from "./CrmTaskModal";
import MarketingEmailModal from "./MarketingEmailModal";
import {
  getEmailTypeLabel,
  getEmailTypeColor,
  getEmailTypeIcon,
  canSendMarketingEmail,
  calculateCartValue,
  getExpiresAtEndOfDay,
  calculateNextThousand,
  formatTimeSinceSent,
} from "../../../helpers/marketingEmails";

const CustomerDrawer = ({ user, visible, onClose, onOrderClick }) => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const firestore = useFirestore();
  const firebase = useFirebase();
  const allUsers = useSelector((state) => state.firestoreReducer.ordered.webshopUsers);
  const carts = useSelector((state) => state.firestoreReducer.ordered.carts);
  const orders = useSelector((state) => state.firestoreReducer.ordered.orders);
  const auth = useSelector((state) => state.firebaseReducer.auth);
  const cmsUsers = useSelector((state) => state.firestoreReducer.ordered.cmsUsers) || [];
  const [messageApi, contextHolder] = message.useMessage();

  const [userCart, setUserCart] = useState(null);
  const [allCustomerOrders, setAllCustomerOrders] = useState(null);

  // Use CRM notes hook for real-time notes
  const { notes: customerNotes, isLoading: customerNotesLoading } = useCrmNotes({
    userId: user?.id,
    autoFetch: visible && !!user?.id,
    realtime: true,
  });
  const [newNoteText, setNewNoteText] = useState("");
  const [newNoteFileList, setNewNoteFileList] = useState([]);
  const [newNoteDateTime, setNewNoteDateTime] = useState(null);
  const [addingNote, setAddingNote] = useState(false);
  const [addNoteModalVisible, setAddNoteModalVisible] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteText, setEditNoteText] = useState("");
  const [editNoteFileList, setEditNoteFileList] = useState([]);
  const [updatingNote, setUpdatingNote] = useState(false);

  const [customerTasks, setCustomerTasks] = useState(null);
  const [customerTasksLoading, setCustomerTasksLoading] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDueAt, setNewTaskDueAt] = useState(null);
  const [newTaskAssigneeUid, setNewTaskAssigneeUid] = useState(null);
  const [addingTask, setAddingTask] = useState(false);
  const [addTaskModalVisible, setAddTaskModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editTaskTitle, setEditTaskTitle] = useState("");
  const [editTaskDueAt, setEditTaskDueAt] = useState(null);
  const [editTaskAssigneeUid, setEditTaskAssigneeUid] = useState(null);
  const [updatingTask, setUpdatingTask] = useState(false);

  const [marketingEmails, setMarketingEmails] = useState([]);
  const [marketingEmailsLoading, setMarketingEmailsLoading] = useState(false);
  const [marketingEmailModalVisible, setMarketingEmailModalVisible] = useState(false);

  const customerOrders = orders?.filter((order) => order.customer.uid === user?.id) || [];

  useEffect(() => {
    if (!visible || !user?.id) {
      setCustomerTasks(null);
      setCustomerTasksLoading(false);
      return;
    }

    setCustomerTasksLoading(true);
    const unsubscribe = firestore
      .collection(FIRESTORE_USERS_TABLE)
      .doc(user.id)
      .collection("crmTasks")
      .orderBy("createdAt", "desc")
      .onSnapshot(
        (snap) => {
          const tasks = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setCustomerTasks(tasks);
          setCustomerTasksLoading(false);
        },
        (err) => {
          console.error(err);
          setCustomerTasksLoading(false);
        },
      );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [visible, user?.id]);

  useEffect(() => {
    if (!visible || !user?.id) {
      setMarketingEmails([]);
      setMarketingEmailsLoading(false);
      return;
    }

    setMarketingEmailsLoading(true);
    const unsubscribe = firestore
      .collection(FIRESTORE_USERS_TABLE)
      .doc(user.id)
      .collection("marketingEmails")
      .orderBy("sentAt", "desc")
      .limit(20)
      .onSnapshot(
        (snap) => {
          const emails = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          setMarketingEmails(emails);
          setMarketingEmailsLoading(false);
        },
        (err) => {
          console.error(err);
          setMarketingEmailsLoading(false);
        },
      );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [visible, user?.id]);

  useEffect(() => {
    if (user && carts) {
      const cart = carts.find((c) => c.id === user.id);
      setUserCart(cart);
    }
  }, [user, carts]);

  const addCustomerNote = async () => {
    if (!newNoteText.trim() && newNoteFileList.length === 0) {
      messageApi.warning(t("please_enter_note_or_attachment"));
      return;
    }

    try {
      setAddingNote(true);
      const suggestedTaskTitle = (newNoteText || "").trim();
      const suggestedTaskDueAt = newNoteDateTime || null;
      const noteRef = firestore.collection(FIRESTORE_USERS_TABLE).doc(user.id).collection("crmNotes").doc();

      const attachments = [];
      for (const file of newNoteFileList) {
        if (file.originFileObj) {
          const storageRef = firebase.storage().ref(`users/${user.id}/crmNotes/${noteRef.id}/${file.name}`);
          await storageRef.put(file.originFileObj);
          const url = await storageRef.getDownloadURL();
          attachments.push({ name: file.name, url, path: storageRef.fullPath });
        }
      }

      const now = dayjs().unix();
      const dateTimeUnix = newNoteDateTime ? dayjs(newNoteDateTime).unix() : now;

      await noteRef.set({
        text: newNoteText.trim(),
        dateTime: dateTimeUnix,
        createdAt: now,
        createdBy: auth.email,
        createdByUid: auth.uid,
        attachments,
      });

      setNewNoteText("");
      setNewNoteFileList([]);
      setNewNoteDateTime(null);
      setAddNoteModalVisible(false);

      setNewTaskTitle(suggestedTaskTitle);
      setNewTaskDueAt(suggestedTaskDueAt);
      setAddTaskModalVisible(true);
      messageApi.success(t("saved"));
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || String(e));
    } finally {
      setAddingNote(false);
    }
  };

  const deleteCustomerNote = async (note) => {
    Modal.confirm({
      title: t("confirm_delete"),
      onOk: async () => {
        try {
          if (note.attachments && Array.isArray(note.attachments)) {
            for (const attachment of note.attachments) {
              if (attachment.path) {
                try {
                  await firebase.storage().ref(attachment.path).delete();
                } catch (err) {
                  console.error("Error deleting attachment:", err);
                }
              }
            }
          }

          await firestore.collection(FIRESTORE_USERS_TABLE).doc(user.id).collection("crmNotes").doc(note.id).delete();
          messageApi.success(t("deleted"));
        } catch (e) {
          console.error(e);
          messageApi.error(e?.message || String(e));
        }
      },
    });
  };

  const openEditNoteModal = (note) => {
    if (!note) return;
    setEditingNote(note);
    setEditNoteText(note.text || "");
    const existingFiles = (note.attachments || []).map((a, idx) => ({
      uid: a.path || `existing-${idx}`,
      name: a.name || "Attachment",
      status: "done",
      url: a.url,
      _existing: true,
      _path: a.path,
    }));
    setEditNoteFileList(existingFiles);
  };

  const updateCustomerNote = async () => {
    if (!editingNote) return;
    if (!editNoteText.trim() && editNoteFileList.length === 0) {
      messageApi.warning(t("please_enter_note_or_attachment"));
      return;
    }

    try {
      setUpdatingNote(true);
      const noteRef = firestore
        .collection(FIRESTORE_USERS_TABLE)
        .doc(user.id)
        .collection("crmNotes")
        .doc(editingNote.id);

      const existingAttachments = editNoteFileList
        .filter((f) => f._existing)
        .map((f) => ({ name: f.name, url: f.url, path: f._path }));
      const newFiles = editNoteFileList.filter((f) => !f._existing);

      for (const file of newFiles) {
        if (file.originFileObj) {
          const storageRef = firebase.storage().ref(`users/${user.id}/crmNotes/${editingNote.id}/${file.name}`);
          await storageRef.put(file.originFileObj);
          const url = await storageRef.getDownloadURL();
          existingAttachments.push({ name: file.name, url, path: storageRef.fullPath });
        }
      }

      const removedAttachments = (editingNote.attachments || []).filter(
        (a) => !editNoteFileList.some((f) => f._path === a.path),
      );
      for (const attachment of removedAttachments) {
        if (attachment.path) {
          try {
            await firebase.storage().ref(attachment.path).delete();
          } catch (err) {
            console.error("Error deleting removed attachment:", err);
          }
        }
      }

      await noteRef.update({
        text: editNoteText.trim(),
        attachments: existingAttachments,
      });

      setEditingNote(null);
      setEditNoteText("");
      setEditNoteFileList([]);
      messageApi.success(t("saved"));
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || String(e));
    } finally {
      setUpdatingNote(false);
    }
  };

  const addCustomerTask = async () => {
    if (!newTaskTitle.trim()) {
      messageApi.warning(t("please_enter_task_title"));
      return;
    }

    try {
      setAddingTask(true);
      const assigneeUid = newTaskAssigneeUid || auth.uid;
      const assignee = cmsUsers.find((u) => u.id === assigneeUid);

      await firestore
        .collection(FIRESTORE_USERS_TABLE)
        .doc(user.id)
        .collection("crmTasks")
        .add({
          title: newTaskTitle.trim(),
          dueAt: newTaskDueAt ? Math.floor(newTaskDueAt.getTime() / 1000) : null,
          status: "open",
          assignedToUid: assigneeUid,
          assignedToEmail: assignee?.email || "",
          createdAt: Math.floor(Date.now() / 1000),
          createdBy: auth.email,
          customerUserId: user.id,
          customerCompanyName: user.company?.name || "",
        });

      setNewTaskTitle("");
      setNewTaskDueAt(null);
      setNewTaskAssigneeUid(null);
      setAddTaskModalVisible(false);
      messageApi.success(t("saved"));
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || String(e));
    } finally {
      setAddingTask(false);
    }
  };

  const markCustomerTaskDone = async (task) => {
    try {
      await firestore
        .collection(FIRESTORE_USERS_TABLE)
        .doc(user.id)
        .collection("crmTasks")
        .doc(task.id)
        .update({
          status: "done",
          completedAt: Math.floor(Date.now() / 1000),
          completedBy: auth.email,
        });
      messageApi.success(t("task_completed"));

      const now = new Date();
      setNewNoteDateTime(now);
      setNewNoteText((prev) => {
        const trimmed = (prev || "").trim();
        if (trimmed) return prev;
        const taskTitle = (task?.title || "").trim();
        return taskTitle ? taskTitle : "";
      });
      setNewNoteFileList([]);
      setAddNoteModalVisible(true);
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || String(e));
    }
  };

  const reopenCustomerTask = async (task) => {
    try {
      await firestore.collection(FIRESTORE_USERS_TABLE).doc(user.id).collection("crmTasks").doc(task.id).update({
        status: "open",
        completedAt: null,
        completedBy: null,
      });
      messageApi.success(t("task_reopened"));
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || String(e));
    }
  };

  const deleteCustomerTask = async (task) => {
    Modal.confirm({
      title: t("confirm_delete"),
      onOk: async () => {
        try {
          await firestore.collection(FIRESTORE_USERS_TABLE).doc(user.id).collection("crmTasks").doc(task.id).delete();
          messageApi.success(t("deleted"));
        } catch (e) {
          console.error(e);
          messageApi.error(e?.message || String(e));
        }
      },
    });
  };

  const openEditTaskModal = (task) => {
    if (!task) return;
    setEditingTask(task);
    setEditTaskTitle(task.title || "");
    setEditTaskDueAt(task.dueAt ? dayjs.unix(task.dueAt).toDate() : null);
    setEditTaskAssigneeUid(task.assignedToUid || null);
  };

  const updateCustomerTask = async () => {
    if (!user?.id || !editingTask?.id) return;
    const trimmed = (editTaskTitle || "").trim();
    if (!trimmed) {
      messageApi.warning(t("enter_required_data"));
      return;
    }

    const dueAtUnix = editTaskDueAt ? dayjs(editTaskDueAt).unix() : null;
    const assignedUid = editTaskAssigneeUid || auth?.uid || "unknown";
    const assignedUser = cmsUsers.find((u) => u.id === assignedUid);
    const assignedEmail = assignedUser?.email || (assignedUid === auth?.uid ? auth?.email : "unknown");

    setUpdatingTask(true);
    try {
      await firestore
        .collection(FIRESTORE_USERS_TABLE)
        .doc(user.id)
        .collection("crmTasks")
        .doc(editingTask.id)
        .update({
          title: trimmed,
          dueAt: dueAtUnix,
          assignedToUid: assignedUid,
          assignedToEmail: assignedEmail,
          updatedAt: dayjs().unix(),
          updatedBy: auth?.email || "unknown",
          updatedByUid: auth?.uid || "unknown",
        });

      setEditingTask(null);
      setEditTaskTitle("");
      setEditTaskDueAt(null);
      setEditTaskAssigneeUid(null);
      messageApi.success(t("saved"));
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || String(e));
    } finally {
      setUpdatingTask(false);
    }
  };

  const retrieveAllCustomerOrders = async () => {
    if (!user?.id) return;
    try {
      const allOrders = await firestore
        .collection("orders")
        .where("customer.uid", "==", user.id)
        .orderBy("createdAt", "desc")
        .get();
      setAllCustomerOrders(allOrders.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || String(e));
    }
  };

  const handleClose = () => {
    setUserCart(null);
    setAllCustomerOrders(null);
    setNewNoteText("");
    setNewNoteFileList([]);
    setNewNoteDateTime(null);
    setAddNoteModalVisible(false);
    setEditingNote(null);
    setEditNoteText("");
    setEditNoteFileList([]);
    setCustomerTasks(null);
    setNewTaskTitle("");
    setNewTaskDueAt(null);
    setNewTaskAssigneeUid(null);
    setAddTaskModalVisible(false);
    setEditingTask(null);
    setEditTaskTitle("");
    setEditTaskDueAt(null);
    setEditTaskAssigneeUid(null);
    onClose();
  };

  const cartColumns = [
    {
      title: t("product_name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("product_code"),
      dataIndex: "code",
      key: "code",
    },
    {
      title: t("quantity"),
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: t("price"),
      dataIndex: "price",
      key: "price",
      align: "right",
      render: (_, product) => (
        <span>
          {CURRENCY}
          {formatPrice(product.price * product.quantity)}
        </span>
      ),
    },
  ];

  if (!user) return null;

  return (
    <>
      {contextHolder}
      <Drawer
        title={user.company?.name}
        placement="right"
        onClose={handleClose}
        open={visible}
        width={isMobile ? "100%" : 720}
        styles={{
          body: {
            padding: isMobile ? "12px" : "24px",
          },
        }}
      >
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label={t("company_name")}>{user.company?.name}</Descriptions.Item>
          <Descriptions.Item label={t("vat_number")}>{user.company?.vatNumber || "-"}</Descriptions.Item>
          <Descriptions.Item label={t("country")}>{user.company?.country}</Descriptions.Item>
          <Descriptions.Item label={t("customer.city")}>{user.company?.city || "-"}</Descriptions.Item>
          {user.address?.delivery && (
            <Descriptions.Item label={t("delivery_address")}>{formatAddress(user.address.delivery)}</Descriptions.Item>
          )}
          {user.address?.invoice && (
            <Descriptions.Item label={t("invoice_address")}>{formatAddress(user.address.invoice)}</Descriptions.Item>
          )}
          <Descriptions.Item label={t("first_name")}>{user.firstName}</Descriptions.Item>
          <Descriptions.Item label={t("last_name")}>{user.lastName}</Descriptions.Item>
          <Descriptions.Item label={t("email")}>{user.email}</Descriptions.Item>
          <Descriptions.Item label={t("phone")}>{user.phoneNumber}</Descriptions.Item>
          <Descriptions.Item label={t("user_type")}>
            {user.isSalesPerson ? t("sales_person") : t("client")}
          </Descriptions.Item>
          {!user.isSalesPerson && (
            <Descriptions.Item label={t("sales_person")}>
              {user.salesPerson ? getSalesPersonNameById(allUsers, user.salesPerson) : "-"}
            </Descriptions.Item>
          )}
          <Descriptions.Item label={t("status")}>
            {user.authorization?.isAuthorized ? t("access") : t("no_access")}
          </Descriptions.Item>
          <Descriptions.Item label={t("review_note")}>{user.authorization?.note || "-"}</Descriptions.Item>
          <Descriptions.Item label={t("last_order")}>
            {user.lastOrderTimestamp ? dayjs.unix(user.lastOrderTimestamp).format("DD-MM-YYYY") : "-"}
          </Descriptions.Item>
        </Descriptions>

        <Divider />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h4 style={{ marginBottom: 0 }}>{t("contact_moments")}</h4>
          <Button type="link" size="small" onClick={() => setAddNoteModalVisible(true)}>
            + {t("add")}
          </Button>
        </div>
        <Card size="small" bodyStyle={{ padding: 0 }}>
          {customerNotesLoading ? (
            <LoadingState>{t("loading")}...</LoadingState>
          ) : !customerNotes || customerNotes.length === 0 ? (
            <EmptyState>{t("no_data")}</EmptyState>
          ) : (
            customerNotes.map((note) => {
              const attachments = Array.isArray(note.attachments) ? note.attachments : [];
              const noteUser = cmsUsers.find((u) => u.id === note.createdByUid);
              const createdByName = noteUser ? noteUser.firstName : "-";
              return (
                <NoteItem key={note.id} onClick={() => openEditNoteModal(note)} title={t("edit")}>
                  <NoteContent>
                    <NoteHeader>
                      <NoteAuthor>{createdByName}</NoteAuthor>
                      <NoteDate>
                        {note.dateTime
                          ? dayjs.unix(note.dateTime).format(DATE_FORMAT_DD_MM_YYYY_HH_mm)
                          : note.createdAt
                            ? dayjs.unix(note.createdAt).format(DATE_FORMAT_DD_MM_YYYY_HH_mm)
                            : "-"}
                      </NoteDate>
                    </NoteHeader>
                    <NoteBody>
                      {note.text && <NoteText hasAttachments={attachments.length > 0}>{note.text}</NoteText>}
                      {attachments.length > 0 && (
                        <Space direction="vertical" size={4} style={{ width: "100%" }}>
                          {attachments.map((a, idx) => (
                            <a
                              key={idx}
                              href={a.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <DownloadOutlined style={{ marginRight: 6 }} />
                              {a.name || a.url}
                            </a>
                          ))}
                        </Space>
                      )}
                    </NoteBody>
                  </NoteContent>
                </NoteItem>
              );
            })
          )}
        </Card>

        <Divider />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h4 style={{ marginBottom: 0 }}>{t("tasks")}</h4>
          <Button type="link" size="small" onClick={() => setAddTaskModalVisible(true)}>
            + {t("add")}
          </Button>
        </div>
        <Card size="small" bodyStyle={{ padding: 0 }}>
          {customerTasksLoading ? (
            <LoadingState>{t("loading")}...</LoadingState>
          ) : !customerTasks || customerTasks.length === 0 ? (
            <EmptyState>{t("no_data")}</EmptyState>
          ) : (
            customerTasks.map((task) => {
              const dueAt = task.dueAt ? dayjs.unix(task.dueAt) : null;
              const isOverdue = dueAt ? dueAt.isBefore(dayjs()) : false;
              const taskUser = cmsUsers.find((u) => u.id === task.assignedToUid);
              const assignedToName = taskUser ? taskUser.firstName || taskUser.email : "-";
              const isDone = task.status === "done";
              return (
                <TaskItem key={task.id} onClick={() => openEditTaskModal(task)} title={!isDone ? t("edit") : ""}>
                  {isDone && (
                    <TaskAvatar>
                      <CheckCircleFilled style={{ color: "#52c41a", fontSize: 20 }} />
                    </TaskAvatar>
                  )}
                  <TaskContent>
                    <TaskHeader>
                      <TaskTitle isDone={isDone}>{task.title}</TaskTitle>
                      <TaskDueDate isOverdue={isOverdue && !isDone} isDone={isDone}>
                        {dueAt ? dueAt.format(DATE_FORMAT_DD_MM_YYYY_HH_mm) : "-"}
                      </TaskDueDate>
                    </TaskHeader>
                    <TaskDescription>
                      <div>{assignedToName ? `${t("assigned_to")}: ${assignedToName}` : ""}</div>
                      {!isDone ? (
                        <Button
                          type="link"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            markCustomerTaskDone(task);
                          }}
                          style={{ padding: 0, height: "auto", marginTop: 2 }}
                        >
                          {t("finish")}
                        </Button>
                      ) : (
                        <Button
                          type="link"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            reopenCustomerTask(task);
                          }}
                          style={{ padding: 0, height: "auto", marginTop: 2, opacity: 0.5 }}
                        >
                          {t("reopen")}
                        </Button>
                      )}
                    </TaskDescription>
                  </TaskContent>
                </TaskItem>
              );
            })
          )}
        </Card>

        <Divider />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h4 style={{ marginBottom: 0 }}>{t("marketing_emails")}</h4>
          <Button
            type="primary"
            size="small"
            icon={<MailOutlined />}
            onClick={() => setMarketingEmailModalVisible(true)}
          >
            {t("send_marketing_email")}
          </Button>
        </div>
        <Card size="small" bodyStyle={{ padding: 0 }}>
          {marketingEmailsLoading ? (
            <LoadingState>{t("loading")}...</LoadingState>
          ) : !marketingEmails || marketingEmails.length === 0 ? (
            <EmptyState>{t("no_marketing_emails_sent")}</EmptyState>
          ) : (
            <div style={{ padding: 16 }}>
              <Timeline>
                {marketingEmails.map((email) => {
                  const daysAgo = dayjs().diff(dayjs.unix(email.sentAt), "day");
                  const isRecent = daysAgo < 7;
                  return (
                    <Timeline.Item
                      key={email.id}
                      color={isRecent ? "red" : "green"}
                      dot={email.status === "sent" ? <CheckCircleFilled /> : <CloseCircleOutlined />}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 16 }}>{getEmailTypeIcon(email.type)}</span>
                        <Tag color={getEmailTypeColor(email.type)}>{getEmailTypeLabel(email.type)}</Tag>
                        <span style={{ fontSize: 12, color: "#999" }}>
                          {dayjs.unix(email.sentAt).format(DATE_FORMAT_DD_MM_YYYY_HH_mm)}
                        </span>
                      </div>
                      {email.type === "discount_5_percent" && email.calculatedThreshold && (
                        <>
                          <div style={{ fontSize: 12, color: "#666" }}>
                            {t("threshold")}: {CURRENCY}
                            {formatPrice(email.calculatedThreshold)}
                          </div>
                          {email.customDiscountPercentage && (
                            <div style={{ fontSize: 12, color: "#666" }}>
                              Korting: {email.customDiscountPercentage}%
                            </div>
                          )}
                        </>
                      )}
                      {typeof email.cartValueAtSend !== "undefined" && (
                        <div style={{ fontSize: 12, color: "#666" }}>
                          {t("cart_value_at_send")}: {CURRENCY}
                          {formatPrice(email.cartValueAtSend)}
                        </div>
                      )}
                      {isRecent && (
                        <Tag color="orange" style={{ marginTop: 4 }}>
                          {formatTimeSinceSent(email.sentAt)}
                        </Tag>
                      )}
                    </Timeline.Item>
                  );
                })}
              </Timeline>
            </div>
          )}
        </Card>

        <Divider />
        <Descriptions bordered size="small" column={1}>
          <Descriptions.Item label={t("status")}>
            {user.authorization?.status ? t(user.authorization.status) : t("open")}
          </Descriptions.Item>
          <Descriptions.Item label={t("status_edited_at")}>
            {user.authorization?.updatedAt ? dayjs.unix(user.authorization.updatedAt).format("DD-MM-YYYY HH:mm") : "-"}
          </Descriptions.Item>
          <Descriptions.Item label={t("in_progress_by")}>{user.authorization?.updatedBy || "-"}</Descriptions.Item>
        </Descriptions>

        {userCart && (
          <div className="pt-5">
            <h4 style={{ margin: 0 }}>{t("cart")}</h4>
            <p>
              {t("cart_last_updated", {
                dateTime: dayjs.unix(userCart.updatedAt).format("DD-MM-YYYY HH:mm"),
              })}
            </p>
            {isMobile ? (
              <>
                <Space direction="vertical" size={8} style={{ width: "100%", marginBottom: 16 }}>
                  {userCart.products.map((product) => (
                    <CartItemCard key={product.id}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600 }}>{product.name}</div>
                        <div style={{ fontSize: 12, marginBottom: 4 }}>{product.code}</div>
                        <div style={{ fontSize: 12, color: "#666" }}>
                          {product.quantity}x {CURRENCY}
                          {formatPrice(product.price)}
                        </div>
                      </div>
                      <div style={{ fontWeight: 600, color: colors.cta }}>
                        {CURRENCY}
                        {formatPrice(product.price * product.quantity)}
                      </div>
                    </CartItemCard>
                  ))}
                </Space>
                <CartSummary>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 16, fontWeight: 600 }}>Totaal</span>
                    <span style={{ fontSize: 18, fontWeight: 700, color: colors.cta }}>
                      {CURRENCY}
                      {formatPrice(userCart.products.reduce((sum, p) => sum + p.price * p.quantity, 0))}
                    </span>
                  </div>
                </CartSummary>
              </>
            ) : (
              <div className="table-responsive">
                <Table
                  columns={cartColumns}
                  dataSource={userCart.products}
                  pagination={false}
                  rowKey="id"
                  className="w-100"
                  bordered={true}
                  summary={(pageData) => {
                    const total = pageData.reduce((sum, product) => sum + product.price * product.quantity, 0);
                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={3}>
                            <strong>Totaal</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align={"right"}>
                            <strong>
                              {CURRENCY}
                              {formatPrice(total)}
                            </strong>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
              </div>
            )}
          </div>
        )}

        <>
          <Divider />
          <h4 style={{ marginBottom: 0 }}>{t("orders")}</h4>
          <p style={{ marginBottom: allCustomerOrders ? 8 : 0 }}>
            {t(allCustomerOrders ? "all_orders" : "orders_30_days")}
          </p>
          {!allCustomerOrders && (
            <a
              style={{ color: "#1890ff", textDecoration: "underline", marginBottom: 8, display: "block" }}
              onClick={retrieveAllCustomerOrders}
            >
              {t("retrieve_more")}
            </a>
          )}
          {isMobile ? (
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
              {(allCustomerOrders || customerOrders || []).map((order) => (
                <OrderCard key={order.id} onClick={() => onOrderClick && onOrderClick(order)}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, color: colors.clickable_text }}>{order.orderId}</span>
                    <Tag color={getPaymentStatusColor(order?.status?.current ?? order.orderStatus)}>
                      {getPaymentStatus(order?.status?.current ?? order.orderStatus)}
                    </Tag>
                  </div>
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
                    {dayjs.unix(order.createdAt).format(DATE_FORMAT_DD_MM_YYYY_HH_mm)}
                  </div>
                  <div style={{ fontWeight: 600, color: "green" }}>{CURRENCY + formatPrice(order.totalPrice)}</div>
                </OrderCard>
              ))}
            </Space>
          ) : (
            <Table
              columns={[
                {
                  title: t("order.order_id"),
                  dataIndex: "orderId",
                  render: (_, order) => (
                    <a
                      onClick={(e) => {
                        e.preventDefault();
                        onOrderClick && onOrderClick(order);
                      }}
                    >
                      {order.orderId}
                    </a>
                  ),
                },
                {
                  title: t("order_date"),
                  dataIndex: "createdAt",
                  render: (data) => <span>{dayjs.unix(data).format(DATE_FORMAT_DD_MM_YYYY_HH_mm)}</span>,
                  defaultSortOrder: "descend",
                },
                {
                  title: t("status"),
                  dataIndex: "orderStatus",
                  render: (_, order) => (
                    <Tag color={getPaymentStatusColor(order?.status?.current ?? order.orderStatus)}>
                      {getPaymentStatus(order?.status?.current ?? order.orderStatus)}
                    </Tag>
                  ),
                },
                {
                  title: t("total"),
                  dataIndex: "totalPrice",
                  render: (_, order) =>
                    order.vat ? (
                      <div style={{ whiteSpace: "nowrap" }}>
                        <span className="font-weight-semibold">{CURRENCY + formatPrice(order.totalPrice)}</span>
                        <span> - ({CURRENCY + formatPrice(order.vat + order.totalPrice)})</span>
                      </div>
                    ) : (
                      <span className="font-weight-semibold">{CURRENCY + formatPrice(order.totalPrice)}</span>
                    ),
                },
              ]}
              dataSource={allCustomerOrders || customerOrders || []}
              pagination={false}
              rowKey="id"
              className="w-100"
              bordered={true}
            />
          )}
        </>
      </Drawer>

      <CrmContactMomentModal
        titleKey="add_contact_moment"
        open={addNoteModalVisible}
        onCancel={() => {
          setAddNoteModalVisible(false);
          setNewNoteText("");
          setNewNoteFileList([]);
          setNewNoteDateTime(null);
        }}
        onOk={addCustomerNote}
        confirmLoading={addingNote}
        customerName={user?.company?.name || ""}
        dateTime={newNoteDateTime}
        onDateTimeChange={setNewNoteDateTime}
        text={newNoteText}
        onTextChange={setNewNoteText}
        fileList={newNoteFileList}
        onFileListChange={setNewNoteFileList}
        dateTimePlaceholderKey="date_time_optional"
        textPlaceholderKey="notes"
      />

      <Modal
        title={t("edit_notes")}
        open={!!editingNote}
        onCancel={() => {
          setEditingNote(null);
          setEditNoteText("");
          setEditNoteFileList([]);
        }}
        onOk={updateCustomerNote}
        okText={t("save")}
        cancelText={t("cancel")}
        confirmLoading={updatingNote}
        footer={[
          <Button
            key="delete"
            danger
            onClick={() => {
              setEditingNote(null);
              setEditNoteText("");
              setEditNoteFileList([]);
              deleteCustomerNote(editingNote);
            }}
          >
            {t("delete")}
          </Button>,
          <Button
            key="cancel"
            onClick={() => {
              setEditingNote(null);
              setEditNoteText("");
              setEditNoteFileList([]);
            }}
          >
            {t("cancel")}
          </Button>,
          <Button key="save" type="primary" loading={updatingNote} onClick={updateCustomerNote}>
            {t("save")}
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 12 }}>
          <Input.TextArea
            value={editNoteText}
            onChange={(e) => setEditNoteText(e.target.value)}
            placeholder={t("notes")}
            autoSize={{ minRows: 4, maxRows: 8 }}
          />
        </div>
        <Upload
          multiple
          beforeUpload={() => false}
          fileList={editNoteFileList}
          onChange={({ fileList }) => setEditNoteFileList(fileList)}
        >
          <Button icon={<PaperClipOutlined />}>{t("add_attachment")}</Button>
        </Upload>
      </Modal>

      <CrmTaskModal
        titleKey="add_tasks"
        open={addTaskModalVisible}
        onCancel={() => {
          setAddTaskModalVisible(false);
          setNewTaskTitle("");
          setNewTaskDueAt(null);
          setNewTaskAssigneeUid(null);
        }}
        onOk={addCustomerTask}
        confirmLoading={addingTask}
        customerName={user?.company?.name || ""}
        title={newTaskTitle}
        onTitleChange={setNewTaskTitle}
        dueAt={newTaskDueAt}
        onDueAtChange={setNewTaskDueAt}
        assigneeUid={newTaskAssigneeUid}
        onAssigneeUidChange={setNewTaskAssigneeUid}
        cmsUsers={cmsUsers}
        defaultAssigneeUid={auth?.uid}
      />

      <CrmTaskModal
        titleKey="edit_tasks"
        open={!!editingTask}
        onCancel={() => {
          setEditingTask(null);
          setEditTaskTitle("");
          setEditTaskDueAt(null);
          setEditTaskAssigneeUid(null);
        }}
        onOk={updateCustomerTask}
        confirmLoading={updatingTask}
        customerName={user?.company?.name || ""}
        footer={[
          <Button
            key="delete"
            danger
            onClick={() => {
              setEditingTask(null);
              setEditTaskTitle("");
              setEditTaskDueAt(null);
              setEditTaskAssigneeUid(null);
              deleteCustomerTask(editingTask);
            }}
          >
            {t("delete")}
          </Button>,
          <Button
            key="cancel"
            onClick={() => {
              setEditingTask(null);
              setEditTaskTitle("");
              setEditTaskDueAt(null);
              setEditTaskAssigneeUid(null);
            }}
          >
            {t("cancel")}
          </Button>,
          <Button key="save" type="primary" loading={updatingTask} onClick={updateCustomerTask}>
            {t("save")}
          </Button>,
        ]}
        title={editTaskTitle}
        onTitleChange={setEditTaskTitle}
        dueAt={editTaskDueAt}
        onDueAtChange={setEditTaskDueAt}
        assigneeUid={editTaskAssigneeUid}
        onAssigneeUidChange={setEditTaskAssigneeUid}
        cmsUsers={cmsUsers}
        defaultAssigneeUid={auth?.uid}
      />

      <MarketingEmailModal
        visible={marketingEmailModalVisible}
        onClose={() => setMarketingEmailModalVisible(false)}
        user={user}
        cart={userCart}
        marketingEmails={marketingEmails}
        onSuccess={() => {
          messageApi.success(t("marketing_email_sent_success"));
        }}
      />
    </>
  );
};

const NoteItem = styled.div`
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafafa;
  }
`;

const NoteContent = styled.div`
  flex: 1;
`;

const NoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const NoteAuthor = styled.span`
  font-weight: 600;
  color: #333;
`;

const NoteDate = styled.span`
  color: #999;
  white-space: nowrap;
  font-size: 13px;
`;

const NoteBody = styled.div`
  color: #666;
`;

const NoteText = styled.div`
  white-space: pre-wrap;
  margin-bottom: ${(props) => (props.hasAttachments ? "8px" : "0")};
`;

const TaskItem = styled.div`
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  cursor: pointer;
  transition: background 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafafa;
  }
`;

const TaskAvatar = styled.div`
  margin-top: 2px;
`;

const TaskContent = styled.div`
  flex: 1;
`;

const TaskHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 4px;
`;

const TaskTitle = styled.span`
  font-weight: 600;
  text-decoration: ${(props) => (props.isDone ? "line-through" : "none")};
  opacity: ${(props) => (props.isDone ? 0.6 : 1)};
`;

const TaskDueDate = styled.span`
  color: ${(props) => (props.isOverdue ? "#ff4d4f" : "#999")};
  white-space: nowrap;
  opacity: ${(props) => (props.isDone ? 0.6 : 1)};
  font-size: 13px;
`;

const TaskDescription = styled.div`
  color: #666;
  font-size: 14px;
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: #999;
`;

const LoadingState = styled.div`
  padding: 24px;
  text-align: center;
  color: #999;
`;

const CartItemCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
`;

const CartSummary = styled.div`
  padding: 16px;
  background: linear-gradient(135deg, #f5f5f5 0%, #fafafa 100%);
  border-radius: 8px;
  border: 2px solid ${colors.cta};
  box-shadow: 0 2px 8px rgba(167, 76, 0, 0.1);
`;

const OrderCard = styled.div`
  padding: 12px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${colors.cta};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &:active {
    transform: scale(0.98);
  }
`;

export default CustomerDrawer;
